import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal, Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import createCreditItemColumns from "../Utils/CreditItemColumns";
import InvoiceFormLayout from "./InvoiceFormLayout";
import {
  getAllPartners,
  getAllProducts,
  getAllCategories,
  getAccountTree,
  getCreditById,
  createCreditWithFile,
  updateCreditWithFile,
  splitCredit,
  clearAccountsCache,
} from "../../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../../core/api/settings";
import { useSelector } from "react-redux";
import { getOffersByBrand } from "../../../../../core/api/marketing";
import dayjs from "dayjs";
import {
  calculateTotals,
  handleProductChange as handleProductChangeUtil,
  handleItemChange as handleItemChangeUtil,
  prepareFormData,
} from "../../../utils/invoiceUtils";
import { convertFromUSD, convertToUSD } from "../../../utils/currencyUtils";
import useDisplayCurrency from "../../../../../hooks/useDisplayCurrency";

const CreateCreditModal = ({
  visible,
  onCancel,
  onSuccess,
  selectedBrand,
  creditId = null,
  defaultCreditType = "bill",
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);
  const { decimalPrecision } = useDisplayCurrency();
  const [creditItems, setCreditItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(selectedBrand);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [customRates, setCustomRates] = useState({});
  const [calculatedValues, setCalculatedValues] = useState({
    calculated_subtotal: 0,
    calculated_taxAmount: 0,
    calculated_discountAmount: 0,
    calculated_total: 0,
    match: true,
  });

  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // State for multiple payment terms - START WITH 1 DEFAULT TERM
  const [paymentTerms, setPaymentTerms] = useState([
    {
      id: Date.now(),
      code: "",
      description: "",
      percentage: 100,
      due_days: 30,
      amount: 0,
      active: true,
    },
  ]);

  useEffect(() => {
    if (visible) {
      try {
        const savedRates = localStorage.getItem("custom_currency_rates");
        if (savedRates) {
          setCustomRates(JSON.parse(savedRates));
        }
      } catch (e) {
        console.error("Failed to load custom rates from localStorage", e);
      }
    }
  }, [visible]);

  // Fetch brands when selectedClients changes
  const fetchBrands = useCallback(async () => {
    if (selectedClients && selectedClients.length > 0) {
      try {
        const clientIds = Array.isArray(selectedClients)
          ? selectedClients.map((c) =>
              typeof c === "object" && c !== null ? c.id : c
            )
          : [
              typeof selectedClients === "object" && selectedClients !== null
                ? selectedClients.id
                : selectedClients,
            ];
        const response = await fetchBrandsBySelectedClients(
          clientIds.filter((id) => id !== undefined && id !== null)
        );
        if (response?.data) {
          setBrands(response.data);
          // If selectedBrand is provided and matches, keep it; otherwise use first brand
          if (selectedBrand) {
            const foundBrand = response.data.find(
              (b) => b.id === selectedBrand.id
            );
            if (foundBrand) {
              setCurrentBrand(foundBrand);
              setFilteredBrands([foundBrand]);
            } else if (response.data.length > 0) {
              setCurrentBrand(response.data[0]);
              setFilteredBrands([response.data[0]]);
            }
          } else if (response.data.length > 0) {
            setCurrentBrand(response.data[0]);
            setFilteredBrands(response.data);
          }
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
        message.error("Failed to fetch brands");
      }
    }
  }, [selectedClients, selectedBrand]);

  const fetchPartners = useCallback(async () => {
    try {
      if (!currentBrand) return; // Don't fetch if no brand selected
      const filters = { brand: currentBrand.id };
      const response = await getAllPartners(filters);
      // Get creditType from form, fallback to defaultCreditType prop
      const creditType = form.getFieldValue("creditType") || defaultCreditType;
      // Filter partners based on credit type
      let filteredPartners = response.data;
      if (creditType === "bill") {
        // For bill credits, only show vendors
        filteredPartners = response.data.filter(
          (partner) =>
            partner.partner_type === "vendor" || partner.partner_type === "both"
        );
      } else if (creditType === "invoice") {
        // For invoice credits, only show customers
        filteredPartners = response.data.filter(
          (partner) =>
            partner.partner_type === "customer" ||
            partner.partner_type === "both"
        );
      }
      setPartners(filteredPartners);
    } catch (error) {
      console.error("Error fetching partners:", error.response || error);
      message.error("Failed to fetch partners");
    }
  }, [currentBrand, defaultCreditType, form]);

  const fetchProducts = useCallback(
    async (vendorId = null) => {
      try {
        // Add brand and vendor filters if selected
        const filters = {};
        if (currentBrand) {
          filters.brand = currentBrand.id;
        }
        if (vendorId) {
          filters.vendor = vendorId;
        }
        const response = await getAllProducts(filters);
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error.response || error);
        message.error("Failed to fetch products");
      }
    },
    [currentBrand, setProducts]
  );

  const fetchCategories = useCallback(async () => {
    try {
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      const response = await getAllCategories(filters);
      if (response && response.data) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error.response || error);
      message.error("Failed to fetch categories");
      setCategories([]);
    }
  }, [currentBrand]);

  const fetchAccounts = useCallback(async () => {
    try {
      // Accounts are GLOBAL - they don't have a brand field
      // The backend ignores brand parameter, so we can fetch accounts without brand
      // Fetch accounts without brand filter (accounts are global)
      const response = await getAccountTree({}); // No filters passed

      // getAccountTree returns response.data directly
      // Handle different response formats for safety
      let accountsData = [];
      if (Array.isArray(response)) {
        accountsData = response;
      } else if (response && Array.isArray(response.data)) {
        accountsData = response.data;
      } else if (response && Array.isArray(response.results)) {
        accountsData = response.results;
      } else if (response && typeof response === "object") {
        // If response is an object but not an array, try to extract data
        accountsData = response.data || response.results || [];
      }

      if (accountsData && accountsData.length > 0) {
        setAccounts(accountsData);
      } else {
        setAccounts([]);
      }
    } catch (error) {
      console.error("Error fetching account tree:", error.response || error);
      message.error(
        `Failed to fetch chart of accounts: ${
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.message ||
          "Unknown error"
        }`
      );
      setAccounts([]);
    }
  }, [currentBrand, selectedBrand]);

  const fetchOffers = useCallback(async () => {
    try {
      if (!currentBrand) return;
      const response = await getOffersByBrand(currentBrand.id);
      setOffers(response.data || []);
    } catch (error) {
      console.error("Error fetching offers:", error.response || error);
      message.error("Failed to fetch offers");
    }
  }, [currentBrand, setOffers]);

  // Refs to track if data needs refresh when modal opens (persists across renders)
  const categoriesNeedRefreshRef = React.useRef(false);
  const accountsNeedRefreshRef = React.useRef(false);
  const partnersNeedRefreshRef = React.useRef(false);
  // Ref to track if credit data has been loaded to prevent infinite loops
  const creditDataLoadedRef = React.useRef(false);

  // Listen for category, account, and partner updates - always listen, refresh when modal opens
  useEffect(() => {
    const handleCategoriesUpdated = () => {
      if (visible && currentBrand) {
        // Refresh immediately if modal is visible
        fetchCategories();
      } else {
        // Mark for refresh when modal opens next
        categoriesNeedRefreshRef.current = true;
      }
    };
    const handleAccountsUpdated = () => {
      // Clear the API cache to ensure fresh data
      clearAccountsCache();
      if (visible) {
        // Refresh immediately if modal is visible
        fetchAccounts();
      } else {
        // Mark for refresh when modal opens next
        accountsNeedRefreshRef.current = true;
      }
    };

    const handlePartnersUpdated = () => {
      if (visible && currentBrand) {
        // Refresh partners immediately if modal is visible
        fetchPartners();
      } else {
        // Mark for refresh when modal opens next
        partnersNeedRefreshRef.current = true;
      }
    };

    window.addEventListener('categoriesUpdated', handleCategoriesUpdated);
    window.addEventListener('accountsUpdated', handleAccountsUpdated);
    window.addEventListener('partnersUpdated', handlePartnersUpdated);

    return () => {
      window.removeEventListener('categoriesUpdated', handleCategoriesUpdated);
      window.removeEventListener('accountsUpdated', handleAccountsUpdated);
      window.removeEventListener('partnersUpdated', handlePartnersUpdated);
    };
  }, [visible, currentBrand, fetchCategories, fetchAccounts, fetchPartners]);

  // Refresh categories/accounts/partners when modal opens if they were updated while closed
  // Use a ref to track if we've already fetched on this modal open
  const hasFetchedOnOpenRef = React.useRef(false);
  useEffect(() => {
    if (visible && !hasFetchedOnOpenRef.current) {
      // Always fetch fresh data when modal opens to ensure latest categories/accounts/partners
      if (currentBrand) {
        fetchCategories();
        fetchPartners();
        // Reset the refresh flags
        categoriesNeedRefreshRef.current = false;
        partnersNeedRefreshRef.current = false;
      }
      fetchAccounts();
      accountsNeedRefreshRef.current = false;
      hasFetchedOnOpenRef.current = true;
    } else if (!visible) {
      // Reset when modal closes
      hasFetchedOnOpenRef.current = false;
    }
  }, [visible, currentBrand, fetchCategories, fetchAccounts, fetchPartners]);

  // Initialize data when modal opens - use ref to prevent multiple runs
  const hasInitializedRef = React.useRef(false);
  useEffect(() => {
    if (visible && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchBrands();

      if (!creditId) {
        // Only reset for create mode; edit mode handled separately
        setIsEditMode(false);
        // Reset form for create mode
        form.resetFields();
        setCreditItems([]);
        setCalculatedValues({
          calculated_subtotal: 0,
          calculated_taxAmount: 0,
          calculated_discountAmount: 0,
          calculated_total: 0,
          match: true,
        });
        // Set default credit type
        form.setFieldsValue({
          creditType: defaultCreditType,
          issue_date: dayjs(),
          currency: "$",
          tax_rate: 0,
          discount_amount: 0,
        });
      }

      // Only fetch these if NOT in edit mode (edit mode will fetch them via loadCreditData)
      if (!creditId) {
        fetchProducts();
        fetchCategories();
        // CRITICAL: Immediately fetch accounts when modal opens
        // Accounts are global, so we don't need a brand to fetch them
        // Use setTimeout to ensure this runs after state updates
        setTimeout(() => {
          fetchAccounts();
        }, 50);
        fetchOffers();
      }

      // If there's an initial currency set, fetch its rate
      const initialCurrency = form.getFieldValue("currency");
      if (
        initialCurrency &&
        initialCurrency !== "$" &&
        initialCurrency !== "USD"
      ) {
        import("../../../utils/currencyUtils").then(
          ({ preloadCurrencyRate }) => {
            preloadCurrencyRate(initialCurrency).catch((err) => {
              console.warn(
                `Failed to preload rate for ${initialCurrency}:`,
                err
              );
            });
          }
        );
      }
    } else if (!visible) {
      // Reset when modal closes
      hasInitializedRef.current = false;
    }
  }, [visible, defaultCreditType, form, creditId, fetchBrands]);

  // Load credit data for editing - MUST be defined before useEffect that uses it
  const loadCreditData = useCallback(
    async (id) => {
      // Prevent multiple simultaneous loads
      if (creditDataLoadedRef.current) {
        return;
      }
      creditDataLoadedRef.current = true;
      setLoading(true);
      try {
        const response = await getCreditById(id);
        const creditData = response;

        // Extract brand from credit data FIRST
        const brandId =
          typeof creditData.brand === "object"
            ? creditData.brand.id
            : creditData.brand;
        const foundBrand = brandId ? brands.find((b) => b.id === brandId) : null;
        
        // Set the brand state so UI shows it
        if (foundBrand) {
          setCurrentBrand(foundBrand);
          setFilteredBrands([foundBrand]);
        }

        const currency = creditData.currency || "$";
        const isUSD = currency === "$" || currency === "USD";

        // Convert amounts from USD to original currency for display if not USD
        const round3 = (val) => Number(Number(val || 0).toFixed(decimalPrecision));
        const displayCreditData = isUSD
          ? creditData
          : {
              ...creditData,
              subtotal: round3(
                convertFromUSD(creditData.subtotal, currency, customRates)
              ),
              discount_amount: round3(
                convertFromUSD(
                  creditData.discount_amount,
                  currency,
                  customRates
                )
              ),
              total_amount: round3(
                convertFromUSD(creditData.total_amount, currency, customRates)
              ),
              items: creditData.items.map((item) => {
                const round3 = (val) => Number(Number(val || 0).toFixed(decimalPrecision));
                return {
                  ...item,
                  unit_price: round3(
                    convertFromUSD(item.unit_price, currency, customRates)
                  ),
                  price: round3(
                    convertFromUSD(item.price, currency, customRates)
                  ), // Also convert price if it exists
                  subtotal: round3(
                    convertFromUSD(item.subtotal, currency, customRates)
                  ),
                  tax_amount: round3(
                    convertFromUSD(item.tax_amount, currency, customRates)
                  ),
                  discount_amount: round3(
                    convertFromUSD(item.discount_amount, currency, customRates)
                  ),
                  total: round3(
                    convertFromUSD(item.total, currency, customRates)
                  ),
                };
              }),
              payment_terms: (creditData.payment_terms || []).map((term) => ({
                ...term,
                amount: round3(
                  convertFromUSD(term.amount, currency, customRates)
                ),
              })),
            };

        // Get credit type from data or default
        const creditType = displayCreditData.credit_type || defaultCreditType;

        // Set credit type in form first so fetchPartners uses the correct filter
        form.setFieldsValue({ creditType: creditType });

        // Fetch partners with correct filter based on credit type FIRST
        // So the dropdown has options before we set the form value
        // Use foundBrand (from credit data) NOT currentBrand (stale state)
        let fetchedPartners = [];
        const targetBrand = foundBrand || currentBrand;
        try {
          if (targetBrand) {
            const filters = { brand: targetBrand.id };
            const response = await getAllPartners(filters);
            // Filter partners based on credit type
            let filteredPartners = response.data || [];
            if (creditType === "bill") {
              filteredPartners = filteredPartners.filter(
                (partner) =>
                  partner.partner_type === "vendor" ||
                  partner.partner_type === "both"
              );
            } else if (creditType === "invoice") {
              filteredPartners = filteredPartners.filter(
                (partner) =>
                  partner.partner_type === "customer" ||
                  partner.partner_type === "both"
              );
            }
            setPartners(filteredPartners);
            fetchedPartners = filteredPartners;
          }
        } catch (error) {
          console.error("Error fetching partners:", error.response || error);
          message.error("Failed to fetch partners");
        }

        // Fetch categories BEFORE loading items so category dropdowns are populated
        await fetchCategories();

        // Fetch products if partner is set
        if (displayCreditData.partner) {
          const partnerId =
            typeof displayCreditData.partner === "object"
              ? displayCreditData.partner.id
              : displayCreditData.partner;
          if (partnerId) {
            await fetchProducts(partnerId);
          }
        }

        // Accounts are global, fetch them without brand filter
        await fetchAccounts();

        // Small delay to ensure state is updated
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Extract partner ID for form (partner might be an object or ID)
        let partnerId =
          displayCreditData.partner?.id || displayCreditData.partner || null;
        // Convert to number if it's a string
        if (partnerId && typeof partnerId === "string") {
          partnerId = parseInt(partnerId, 10);
        }
        // Convert to number if it's not already
        if (partnerId && typeof partnerId !== "number" && !isNaN(partnerId)) {
          partnerId = Number(partnerId);
        }

        // Populate form fields AFTER partners are fetched
        form.setFieldsValue({
          creditType: creditType,
          number: displayCreditData.number,
          partner: partnerId, // Use extracted partner ID
          brand: displayCreditData.brand?.id || displayCreditData.brand,
          issue_date: displayCreditData.issue_date
            ? dayjs(displayCreditData.issue_date)
            : null,
          subtotal: displayCreditData.subtotal,
          discount_amount: displayCreditData.discount_amount || 0,
          tax_rate: displayCreditData.tax_rate || 0,
          total_amount: displayCreditData.total_amount,
          currency: displayCreditData.currency || "$",
          notes: displayCreditData.notes,
        });

        // Set partner for filtering brands - use fetchedPartners from the API call above
        if (displayCreditData.partner) {
          const partnerObj =
            typeof displayCreditData.partner === "object"
              ? displayCreditData.partner
              : null;
          // If we have a partner object, use it; otherwise find it in fetched partners list
          if (partnerObj) {
            setSelectedPartner(partnerObj);
          } else if (partnerId) {
            // Find partner in the fetched partners list (use fetchedPartners from above, not state)
            const foundPartner = fetchedPartners.find(
              (p) => p.id === partnerId
            );
            if (foundPartner) {
              setSelectedPartner(foundPartner);
            } else {
              setSelectedPartner(partnerId);
            }
          }
        }

        // Set items
        let formattedItems = [];
        if (displayCreditData.items && displayCreditData.items.length > 0) {
          formattedItems = displayCreditData.items.map((item, index) => ({
            key: index,
            productId: item.product?.id || item.product || null,
            productName: item.product_name || "",
            productVariation: item.product_variation || "",
            description: item.description || "",
            quantity: item.quantity || 1,
            unitPrice: item.unit_price || item.price || 0,
            subtotal: item.subtotal || 0,
            taxRate: item.tax_rate || 0,
            taxAmount: item.tax_amount || 0,
            discountAmount: item.discount_amount || 0,
            total: item.total || item.subtotal || 0,
            categoryId: item.category?.id || item.category || null,
            accountId: item.account?.id || item.account || null,
            offerId: item.offer?.id || item.offer || null,
            offerIds: item.offer?.id ? [item.offer.id] : [],
            // Payment terms per line item
            // Payment terms per line item (array of payment terms)
            payment_terms: item.payment_terms || [],
          }));
          setCreditItems(formattedItems);
          // Calculate totals after loading items (use setTimeout to ensure state is updated)
          // Note: calculateCreditTotals will be called via useEffect when creditItems change
          setTimeout(() => {
            // Use calculateTotals directly from utils instead of the callback
            const totals = calculateTotals(formattedItems, form);
            setCalculatedValues(totals);
            form.setFieldsValue({
              subtotal: totals.calculated_subtotal,
              total_amount: totals.calculated_total,
            });
          }, 100);
        }

        // Load credit-level payment terms
        // Calculate totals directly using utils
        const totals = calculateTotals(formattedItems || [], form);
        const calculatedValues = {
          calculated_subtotal: totals.calculated_subtotal,
          calculated_taxAmount: totals.calculated_taxAmount,
          calculated_discountAmount: totals.calculated_discountAmount,
          calculated_total: totals.calculated_total,
          match: totals.match,
        };
        if (
          displayCreditData.payment_terms &&
          Array.isArray(displayCreditData.payment_terms) &&
          displayCreditData.payment_terms.length > 0
        ) {
          // Use calculated total instead of form total_amount
          const calculatedTotal = calculatedValues.calculated_total || 0;

          const updatedPaymentTerms = displayCreditData.payment_terms.map(
            (term) => {
              const amount = (term.percentage / 100) * calculatedTotal;
              return {
                id: term.id || Date.now(),
                code: term.code || "",
                description: term.description || "",
                percentage: term.percentage || 100,
                due_days: term.due_days ?? 30, // Use ?? to allow 0 as valid value
                amount: amount,
                active: term.active !== undefined ? term.active : true,
              };
            }
          );

          setPaymentTerms(updatedPaymentTerms);
        } else {
          // If no credit-level payment terms, set default
          const calculatedTotal = calculatedValues.calculated_total || 0;
          setPaymentTerms([
            {
              id: Date.now(),
              code: "",
              description: "",
              percentage: 100,
              due_days: 30,
              amount: calculatedTotal,
              active: true,
            },
          ]);
        }
      } catch (error) {
        console.error("Error loading credit:", error);
        message.error("Failed to load credit data");
        creditDataLoadedRef.current = false; // Reset on error so user can retry
      } finally {
        setLoading(false);
      }
    },
    [
      brands,
      form,
      currentBrand,
      fetchCategories,
      fetchAccounts,
      fetchProducts,
      defaultCreditType,
      customRates,
      decimalPrecision,
    ]
  );

  // Load credit data when editing (creditId is provided)
  useEffect(() => {
    // Reset the loaded flag when modal closes or creditId changes
    if (!visible || !creditId) {
      creditDataLoadedRef.current = false;
      return;
    }
    
    // Only load if modal is visible, creditId exists, brands are loaded, and we haven't loaded yet
    if (visible && creditId && brands.length > 0 && !creditDataLoadedRef.current) {
      setIsEditMode(true);
      loadCreditData(creditId);
    }
  }, [visible, creditId, brands.length, loadCreditData]);

  const handleBrandChange = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    setCurrentBrand(brand);
    // Clear dependent data when brand changes
    setPartners([]);
    setProducts([]);
    setCategories([]);
    // DON'T clear accounts - they're global and should always be available
    // setAccounts([]);
    setOffers([]);
    // Fetch partners immediately when brand changes
    if (brand) {
      // Get creditType from form, fallback to defaultCreditType prop
      const creditType = form.getFieldValue("creditType") || defaultCreditType;
      const filters = { brand: brand.id };
      getAllPartners(filters)
        .then((response) => {
          // Filter partners based on credit type
          let filteredPartners = response.data;
          if (creditType === "bill") {
            // For bill credits, only show vendors
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "vendor" ||
                partner.partner_type === "both"
            );
          } else if (creditType === "invoice") {
            // For invoice credits, only show customers
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "customer" ||
                partner.partner_type === "both"
            );
          }
          setPartners(filteredPartners);
        })
        .catch((error) => {
          console.error("Error fetching partners:", error.response || error);
          message.error("Failed to fetch partners");
        });

      // CRITICAL: Fetch accounts immediately when brand changes
      // This ensures chart of accounts dropdown is populated right away
      // Note: Accounts are global, but we still fetch them when brand changes
      fetchAccounts().catch((error) => {
        // Error already handled in fetchAccounts
      });
    }
  };

  const handlePartnerChange = (value) => {
    setSelectedPartner(value);

    // Filter brands based on selected partner
    const partner = partners.find((p) => p.id === value);
    if (partner && partner.brands) {
      // Filter available brands to only those associated with this partner
      const partnerBrandIds = partner.brands.map((b) =>
        typeof b === "object" ? b.id : b
      );
      const partnerBrands = brands.filter((b) =>
        partnerBrandIds.includes(b.id)
      );

      setFilteredBrands(partnerBrands);

      // If current brand is not in partner's brands, switch to first available
      if (
        currentBrand &&
        !partnerBrandIds.includes(currentBrand.id) &&
        partnerBrands.length > 0
      ) {
        setCurrentBrand(partnerBrands[0]);
        form.setFieldsValue({ brand: partnerBrands[0].id });
      }
    } else {
      // No partner brands, show all brands
      setFilteredBrands(brands);
    }

    // Fetch products for this vendor
    if (value) {
      fetchProducts(value);
    }
  };

  const addItem = () => {
    const newKey =
      creditItems.length > 0 ? creditItems[creditItems.length - 1].key + 1 : 0;
    const newItem = {
      key: newKey,
      productId: null,
      productName: "",
      productVariation: "",
      description: "",
      quantity: 1,
      unitPrice: 0,
      subtotal: 0,
      taxRate: 0,
      taxAmount: 0,
      discountAmount: 0,
      total: 0,
      categoryId: null,
      accountId: null,
      offerId: null,
      offerIds: [],
      // Payment terms per line item
      // Payment terms per line item (array of payment terms)
      payment_terms: [],
    };
    const updatedItems = [...creditItems, newItem];
    setCreditItems(updatedItems);
    // Recalculate totals after adding item
    setTimeout(() => {
      calculateCreditTotals(updatedItems);
    }, 0);
  };

  const removeItem = (key) => {
    const updatedItems = creditItems.filter((item) => item.key !== key);
    setCreditItems(updatedItems);
    // Recalculate totals after removing item
    calculateCreditTotals(updatedItems);
  };

  const handleProductChange = (value, key) => {
    const updatedItems = handleProductChangeUtil(
      value,
      key,
      products,
      creditItems
    );
    if (updatedItems !== creditItems) {
      setCreditItems(updatedItems);
      // Recalculate totals after product change
      calculateCreditTotals(updatedItems);
    }
  };

  const handleItemChange = (key, field, value) => {
    const updatedItems = handleItemChangeUtil(key, field, value, creditItems);
    setCreditItems(updatedItems);
    // Recalculate totals after item change
    calculateCreditTotals(updatedItems);
  };

  const handleCreditError = (error, isEditMode) => {
    console.error(
      `Error ${isEditMode ? "updating" : "creating"} credit:`,
      error.response || error
    );

    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (typeof errorData === "object" && !Array.isArray(errorData)) {
        Object.keys(errorData).forEach((key) => {
          const errorMessages = Array.isArray(errorData[key])
            ? errorData[key].join(", ")
            : errorData[key];
          message.error(`${key}: ${errorMessages}`);
        });
      } else {
        message.error(
          `Failed to ${isEditMode ? "update" : "create"} credit: ${errorData}`
        );
      }
    } else {
      message.error(
        `Failed to ${isEditMode ? "update" : "create"} credit: ${
          error.message || "Unknown error"
        }`
      );
    }
  };

  const calculateCreditTotals = useCallback(
    (items, formInstance) => {
      const calculatedValues = calculateTotals(items, formInstance || form);
      setCalculatedValues(calculatedValues);

      // Update form fields immediately with calculated values
      if (formInstance || form) {
        const formToUpdate = formInstance || form;
        formToUpdate.setFieldsValue({
          subtotal: calculatedValues.calculated_subtotal,
          total_amount: calculatedValues.calculated_total,
        });
      }

      // Use the calculated total instead of form total_amount
      const calculatedTotal = calculatedValues.calculated_total || 0;

      if (paymentTerms.length > 0) {
        const updatedTerms = paymentTerms.map((term) => {
          return {
            ...term,
            amount: (term.percentage / 100) * calculatedTotal,
          };
        });
        setPaymentTerms(updatedTerms);
      }

      return calculatedValues;
    },
    [form, paymentTerms, setCalculatedValues, setPaymentTerms]
  );

  // Payment terms management
  const addPaymentTerm = () => {
    const newTermsCount = paymentTerms.length + 1;
    const evenPercentage = Math.floor(100 / newTermsCount);
    const remainder = 100 % newTermsCount;

    // Use calculated total instead of form total_amount
    const calculatedTotal = calculatedValues.calculated_total || 0;

    // Rebuild all terms with updated percentage
    const updatedTerms = paymentTerms.map((term, index) => {
      const percent =
        evenPercentage + (index === newTermsCount - 1 ? remainder : 0);
      return {
        ...term,
        percentage: percent,
        amount: (percent / 100) * calculatedTotal,
        due_days: 30 * (index + 1),
      };
    });

    // Add the new term
    const newPercentage =
      evenPercentage +
      (paymentTerms.length === newTermsCount - 1 ? remainder : 0);
    updatedTerms.push({
      id: Date.now(),
      code: "",
      description: "",
      percentage: newPercentage,
      due_days: 30 * newTermsCount,
      amount: (newPercentage / 100) * calculatedTotal,
      active: true,
    });

    setPaymentTerms(updatedTerms);
  };

  const removePaymentTerm = (id) => {
    const remainingTerms = paymentTerms.filter((term) => term.id !== id);

    // If no terms left, don't redistribute
    if (remainingTerms.length === 0) {
      setPaymentTerms([]);
      return;
    }

    // Redistribute percentages evenly among remaining terms
    const basePercentage = Math.floor(100 / remainingTerms.length);
    const remainder = 100 - basePercentage * remainingTerms.length;

    // Use calculated total instead of form total_amount
    const calculatedTotal = calculatedValues.calculated_total || 0;

    const redistributedTerms = remainingTerms.map((term, index) => {
      // Give the remainder to the last term to ensure total is exactly 100%
      const percentage =
        basePercentage + (index === remainingTerms.length - 1 ? remainder : 0);
      return {
        ...term,
        percentage,
        amount: (percentage / 100) * calculatedTotal,
      };
    });

    setPaymentTerms(redistributedTerms);
  };

  const updatePaymentTerm = (id, field, value) => {
    if (field === "percentage") {
      const otherTermsTotal = paymentTerms.reduce((sum, term) => {
        return term.id !== id ? sum + parseFloat(term.percentage || 0) : sum;
      }, 0);

      if (otherTermsTotal + parseFloat(value || 0) > 100) {
        const maxAllowed = 100 - otherTermsTotal;
        message.warning(
          `Total percentage cannot exceed 100%. Setting to maximum allowed: ${maxAllowed.toFixed(
            2
          )}%`
        );
        value = maxAllowed;
      }
    }

    // Use calculated total instead of form total_amount
    const calculatedTotal = calculatedValues.calculated_total || 0;

    const updatedTerms = paymentTerms.map((term) => {
      if (term.id === id) {
        const updatedTerm = { ...term, [field]: value };

        if (field === "percentage") {
          updatedTerm.amount = (value / 100) * calculatedTotal;
        }

        return updatedTerm;
      }
      return term;
    });
    setPaymentTerms(updatedTerms);
  };

  const getCurrentCurrency = () =>
    form ? form.getFieldValue("currency") || "$" : "$";

  // Handle split credit
  const handleSplitCredit = async () => {
    if (!currentBrand) {
      message.error("Please select a brand before splitting the credit");
      return;
    }

    if (creditItems.length === 0) {
      message.error("Please add at least one item before splitting");
      return;
    }

    // Check if items have different payment terms
    const paymentTermGroups = new Set();
    creditItems.forEach((item) => {
      const paymentTerms = item.payment_terms || [];
      if (paymentTerms.length === 0) {
        paymentTermGroups.add("default-100-30");
      } else {
        // Create a unique key for each combination of payment terms
        const termsKey = paymentTerms
          .map((pt) => `${pt.percentage}-${pt.due_days}`)
          .sort()
          .join("|");
        paymentTermGroups.add(termsKey);
      }
    });

    if (paymentTermGroups.size <= 1) {
      message.warning(
        "All items have the same payment terms. No split needed."
      );
      return;
    }

    Modal.confirm({
      title: "Split Credit",
      content: `This will split the credit into ${paymentTermGroups.size} separate credits based on payment terms. The original credit will be deleted. Continue?`,
      okText: "Yes, Split",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);
        try {
          let creditIdToSplit = creditId;

          // If it's a new credit, save it first
          if (!isEditMode || !creditId) {
            const values = form.getFieldsValue();
            // Prepare credit data
            const creditData = {
              ...values,
              credit_type: values.creditType,
              issue_date: values.issue_date
                ? values.issue_date.format("YYYY-MM-DD")
                : null,
              brand: currentBrand?.id || values.brand,
              partner: values.partner ? Number(values.partner) : null,
              subtotal: Number(
                Number(
                  calculatedValues?.calculated_subtotal || values.subtotal || 0
                ).toFixed(3)
              ),
              discount_amount: Number(
                Number(
                  calculatedValues?.calculated_discount_amount ||
                    values.discount_amount ||
                    0
                ).toFixed(3)
              ),
              tax_rate: Number((parseFloat(values.tax_rate) || 0).toFixed(3)),
              total_amount: Number(
                Number(
                  calculatedValues?.calculated_total || values.total_amount || 0
                ).toFixed(3)
              ),
              items: creditItems.map((item) => ({
                product: item.productId,
                category: item.categoryId,
                account: item.accountId,
                offer: item.offerId,
                description: item.description || "",
                quantity: Number(item.quantity || 1),
                // Payment terms per line item (array of payment terms)
                payment_terms: item.payment_terms || [],
                unit_price: Number(Number(item.unitPrice || 0).toFixed(3)),
                tax_rate: Number((parseFloat(item.taxRate) || 0).toFixed(3)),
                tax_amount: Number(Number(item.taxAmount || 0).toFixed(3)),
                discount_amount: Number(
                  Number(item.discountAmount || 0).toFixed(3)
                ),
                subtotal: Number(Number(item.subtotal || 0).toFixed(3)),
                total: Number(
                  Number(item.total || item.subtotal || 0).toFixed(3)
                ),
              })),
            };
            delete creditData.creditType;
            const formData = prepareFormData(creditData, values);
            formData.append("payment_terms", JSON.stringify(paymentTerms));
            const response = await createCreditWithFile(formData);
            creditIdToSplit = response.data.id;
            message.success("Credit created. Splitting now...");
          }

          // Split the credit
          const splitResponse = await splitCredit(creditIdToSplit);
          const splitCredits = splitResponse.split_credits || [];

          Modal.success({
            title: "Credit Split Successfully",
            width: 500,
            content: (
              <div>
                <p>Credit split into {splitCredits.length} credits:</p>
                <ul style={{ marginTop: 16 }}>
                  {splitCredits.map((cr) => (
                    <li key={cr.id} style={{ marginBottom: 8 }}>
                      <strong>{cr.number}</strong> - $
                      {parseFloat(cr.total_amount).toFixed(decimalPrecision)} (
                      {cr.payment_term})
                    </li>
                  ))}
                </ul>
              </div>
            ),
            onOk: () => {
              onSuccess?.();
              handleCancel();
            },
          });
        } catch (error) {
          console.error("Error splitting credit:", error);
          const errorMsg =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Failed to split credit";
          message.error(errorMsg);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const onFinish = async (values) => {
    // Validate that all items have descriptions
    const itemsWithoutDescription = creditItems.filter(
      (item) => !item.description || item.description.trim() === ""
    );
    if (itemsWithoutDescription.length > 0) {
      message.error(
        `Please add a description for all line items. ${itemsWithoutDescription.length} item(s) missing description.`
      );
      return;
    }

    // Check for duplicate payment terms (same percentage AND due_days)
    if (paymentTerms.length > 0) {
      const termKeys = paymentTerms.map(
        (term) =>
          `${parseFloat(term.percentage || 0).toFixed(3)}-${term.due_days}`
      );
      const uniqueKeys = new Set(termKeys);
      if (uniqueKeys.size !== termKeys.length) {
        message.error(
          "Duplicate payment terms found. Each combination of percentage and due days must be unique."
        );
        return;
      }
    }

    try {
      setLoading(true);

      // Validate that all items have required fields
      const invalidItems = creditItems.filter((item) => {
        // Backend requires account, unit_price, and description
        return (
          !item.accountId ||
          !item.unitPrice ||
          item.unitPrice <= 0 ||
          !item.description ||
          item.description.trim() === ""
        );
      });

      if (invalidItems.length > 0) {
        message.error(
          `Please ensure all items have a Chart of Account selected, a valid unit price (greater than 0), and a description.`
        );
        setLoading(false);
        return;
      }

      await submitCredit(values);
    } catch (error) {
      handleCreditError(error, isEditMode);
    } finally {
      setLoading(false);
    }
  };

  const submitCredit = async (values) => {
    // CRITICAL: Convert ALL amounts to USD if currency is not USD
    // Everything in the database should be stored in USD for consistency
    const currency = values.currency || "$";
    const isUSD = currency === "$" || currency === "USD";

    const round3 = (val) => Number(Number(val || 0).toFixed(3));

    let finalPaymentTerms = paymentTerms;
    let finalCreditItems = creditItems;

    if (!isUSD) {
        // Convert credit-level payment terms amounts to USD and round to 3 decimals
      finalPaymentTerms = paymentTerms.map((term) => ({
        ...term,
        amount: round3(convertToUSD(term.amount || 0, currency, customRates)),
      }));

        // Convert item-level amounts to USD and round to 3 decimals
      finalCreditItems = creditItems.map((item) => ({
        ...item,
        unitPrice: round3(
          convertToUSD(item.unitPrice || 0, currency, customRates)
        ),
        subtotal: round3(
          convertToUSD(item.subtotal || 0, currency, customRates)
        ),
        taxAmount: round3(
          convertToUSD(item.taxAmount || 0, currency, customRates)
        ),
        discountAmount: round3(
          convertToUSD(item.discountAmount || 0, currency, customRates)
        ),
        total: round3(
          convertToUSD(item.total || item.subtotal || 0, currency, customRates)
        ),
        // Convert item payment terms amounts to USD
        payment_terms: (item.payment_terms || []).map((term) => ({
          ...term,
          amount: round3(convertToUSD(term.amount || 0, currency, customRates)),
        })),
      }));
    }

    // Prepare credit data with proper field mapping
    // CRITICAL: Ensure required fields have defaults
    const creditType = values.creditType || defaultCreditType;
    const issueDate = values.issue_date 
      ? values.issue_date.format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD"); // Default to today if missing
    
    const creditData = {
      ...values,
      // Map camelCase field names to snake_case for backend
      credit_type: creditType,
      issue_date: issueDate,
      brand: currentBrand?.id || values.brand,
      // Ensure partner is a number (ID)
      partner: values.partner ? Number(values.partner) : null,
      // Round monetary values to 2 decimal places (already in USD if converted)
      // Use calculatedValues or form values for credit-level totals
      subtotal: (() => {
        const creditSubtotal =
          calculatedValues?.calculated_subtotal || values.subtotal || 0;
        return round3(
          isUSD
            ? creditSubtotal
            : convertToUSD(creditSubtotal, currency, customRates)
        );
      })(),
      discount_amount: (() => {
        const creditDiscountAmount =
          calculatedValues?.calculated_discount_amount ||
          values.discount_amount ||
          0;
        return round3(
          isUSD
            ? creditDiscountAmount
            : convertToUSD(creditDiscountAmount, currency, customRates)
        );
      })(),
      tax_rate: Number((parseFloat(values.tax_rate) || 0).toFixed(3)),
      total_amount: (() => {
        const creditTotalAmount =
          calculatedValues?.calculated_total || values.total_amount || 0;
        return round3(
          isUSD
            ? creditTotalAmount
            : convertToUSD(creditTotalAmount, currency, customRates)
        );
      })(),
      items: finalCreditItems.map((item) => ({
        product: item.productId,
        category: item.categoryId,
        account: item.accountId, // CRITICAL: Backend requires account, cannot be null
        offer: item.offerId,
        description: item.description || "",
        quantity: Number(item.quantity || 1),
        // Payment terms per line item (already converted to USD if needed)
        payment_terms: item.payment_terms || [],
        // Round unit_price to 3 decimal places (already in USD if converted)
        unit_price: round3(item.unitPrice || 0),
        tax_rate: Number((parseFloat(item.taxRate) || 0).toFixed(3)),
        tax_amount: round3(item.taxAmount || 0),
        discount_amount: round3(item.discountAmount || 0),
        subtotal: round3(item.subtotal || 0),
        total: round3(item.total || item.subtotal || 0),
      })),
    };

    // Remove camelCase fields that have been converted to snake_case
    delete creditData.creditType;

    // Prepare FormData for file upload
    const formData = new FormData();

    // Append all credit data fields to FormData
    Object.keys(creditData).forEach((key) => {
      if (key === "items") {
        // For nested arrays, we need to stringify
        formData.append(key, JSON.stringify(creditData[key]));
      } else if (creditData[key] !== undefined && creditData[key] !== null) {
        formData.append(key, creditData[key]);
      }
    });

    // Append payment terms (already converted to USD if needed)
    formData.append("payment_terms", JSON.stringify(finalPaymentTerms));

    try {
      let response;
      if (isEditMode && creditId) {
        response = await updateCreditWithFile(creditId, formData);
        message.success("Credit note updated successfully!");
      } else {
        response = await createCreditWithFile(formData);
        message.success("Credit note created successfully!");
      }

      // Call onSuccess callback
      if (onSuccess) {
        onSuccess(response);
      }

      // Close modal
      handleCancel();
    } catch (error) {
      handleCreditError(error, isEditMode);
      throw error;
    }
  };

  const onValuesChange = (changedValues) => {
    // Fetch fresh exchange rate when currency changes (non-blocking)
    if ("currency" in changedValues && changedValues.currency) {
      const newCurrency = changedValues.currency;
      if (newCurrency !== "$" && newCurrency !== "USD") {
        // Fetch fresh rate for the new currency (fire and forget)
        import("../../../utils/currencyUtils")
          .then(({ preloadCurrencyRate }) => preloadCurrencyRate(newCurrency))
          .catch((err) => {
            console.warn(`Failed to preload rate for ${newCurrency}:`, err);
          });
      }
    }

    // Recalculate totals when form values change (tax_rate, discount_amount, etc.)
    if (
      "discount_amount" in changedValues ||
      "tax_rate" in changedValues ||
      "total_amount" in changedValues
    ) {
      calculateCreditTotals(creditItems);
    }

    // If creditType changed, refetch partners with new filter
    if (changedValues.creditType !== undefined) {
      // Get the new creditType from the form (use changed value, fallback to form value, then default)
      const newCreditType =
        changedValues.creditType ||
        form.getFieldValue("creditType") ||
        defaultCreditType;
      // Refetch partners with the new creditType filter
      const filters = currentBrand ? { brand: currentBrand.id } : {};
      getAllPartners(filters)
        .then((response) => {
          // Filter partners based on the new credit type
          let filteredPartners = response.data;
          if (newCreditType === "bill") {
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "vendor" ||
                partner.partner_type === "both"
            );
          } else if (newCreditType === "invoice") {
            filteredPartners = response.data.filter(
              (partner) =>
                partner.partner_type === "customer" ||
                partner.partner_type === "both"
            );
          }
          setPartners(filteredPartners);
        })
        .catch((error) => {
          console.error("Error fetching partners:", error.response || error);
          message.error("Failed to fetch partners");
        });
    }
  };

  // Columns for items table
  const columns = useMemo(
    () =>
      createCreditItemColumns(
        products,
        categories,
        accounts,
        offers,
        handleProductChange,
        handleItemChange,
        getCurrentCurrency,
        setCreditItems,
        calculateCreditTotals,
        removeItem,
        getCurrentCurrency(),
        navigate,
        [], // Credits don't have payment terms at invoice level, so pass empty array
        () => {
          const issueDateValue = form.getFieldValue("issue_date");
          return issueDateValue ? issueDateValue.format("YYYY-MM-DD") : null;
        }
      ),
    [
      products,
      categories,
      accounts,
      offers,
      handleProductChange,
      handleItemChange,
      form,
      getCurrentCurrency,
      setCreditItems,
      calculateCreditTotals,
      removeItem,
      navigate,
    ]
  );

  const handleCancel = () => {
    form.resetFields();
    setCreditItems([]);
    setPaymentTerms([
      {
        id: Date.now(),
        code: "",
        description: "",
        percentage: 100,
        due_days: 30,
        amount: 0,
        active: true,
      },
    ]);
    setCalculatedValues({
      calculated_subtotal: 0,
      calculated_taxAmount: 0,
      calculated_discountAmount: 0,
      calculated_total: 0,
      match: true,
    });
    setSelectedPartner(null);
    setIsEditMode(false);
    // Reset all refs when modal closes
    creditDataLoadedRef.current = false;
    hasInitializedRef.current = false;
    hasFetchedOnOpenRef.current = false;
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Modal
      title={isEditMode ? "Edit Credit Note" : "Create Credit Note"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="95%"
      style={{ top: 20 }}
      zIndex={2000}
      destroyOnClose
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
        <InvoiceFormLayout
          form={form}
          isEditMode={isEditMode}
          loading={loading}
          invoiceItems={creditItems}
          setInvoiceItems={setCreditItems}
          partners={partners}
          paymentTerms={paymentTerms}
          setPaymentTerms={setPaymentTerms}
          categories={categories}
          products={products}
          accounts={accounts}
          offers={offers}
          columns={columns}
          calculatedValues={calculatedValues}
          setCalculatedValues={setCalculatedValues}
          onFinish={onFinish}
          handlePartnerChange={handlePartnerChange}
          handleProductChange={handleProductChange}
          handleItemChange={handleItemChange}
          calculateInvoiceTotals={calculateCreditTotals}
          addItem={addItem}
          removeItem={removeItem}
          addPaymentTerm={addPaymentTerm}
          removePaymentTerm={removePaymentTerm}
          updatePaymentTerm={updatePaymentTerm}
          navigate={navigate}
          selectedBrand={currentBrand}
          onValuesChange={onValuesChange}
          marketingFiles={[]}
          brands={filteredBrands}
          allBrands={brands}
          onBrandChange={handleBrandChange}
          defaultInvoiceType={defaultCreditType === "bill" ? "bill" : "invoice"}
          fileList={[]}
          onSplitInvoice={handleSplitCredit}
          splitButtonText="Split Credit"
        />
      </div>
    </Modal>
  );
};

export default CreateCreditModal;
